import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Analytics = () => {
  const [activeChart, setActiveChart] = useState(0);

  const charts = [
    {
      id: 'growth',
      title: 'Growth & Engagement',
      subtitle: '2024 Performance',
      icon: '📈',
      type: 'line'
    },
    {
      id: 'platform',
      title: 'Platform Reach',
      subtitle: 'Monthly Average',
      icon: '📱',
      type: 'bar'
    },
    {
      id: 'partnerships',
      title: 'Partnership Distribution',
      subtitle: 'Q4 2024',
      icon: '🤝',
      type: 'doughnut'
    }
  ];

  // Growth Analytics Data
  const growthData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Follower Growth',
        data: [1200, 1900, 3000, 5000, 8500, 12000, 15800, 20500, 26000, 32500, 40000, 48000],
        fill: true,
        backgroundColor: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
        borderColor: '#6366f1',
        borderWidth: 3,
        pointBackgroundColor: '#6366f1',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 3,
        pointRadius: 6,
        tension: 0.4,
      },
      {
        label: 'Engagement Rate',
        data: [2.5, 3.1, 4.2, 5.8, 6.9, 7.5, 8.2, 8.9, 9.1, 9.8, 10.2, 11.5],
        fill: true,
        backgroundColor: 'rgba(6, 182, 212, 0.1)',
        borderColor: '#06b6d4',
        borderWidth: 3,
        pointBackgroundColor: '#06b6d4',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 3,
        pointRadius: 6,
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  };

  // Platform Performance Data
  const platformData = {
    labels: ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'LinkedIn'],
    datasets: [
      {
        label: 'Reach (Millions)',
        data: [12.5, 18.3, 8.7, 6.2, 4.1],
        backgroundColor: [
          '#6366f1',
          '#ec4899',
          '#06b6d4',
          '#10b981',
          '#f59e0b',
        ],
        borderColor: [
          '#4f46e5',
          '#db2777',
          '#0891b2',
          '#059669',
          '#d97706',
        ],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  // Partnership Distribution Data
  const partnershipData = {
    labels: ['Brand Partnerships', 'Content Collaborations', 'Strategic Alliances', 'Consulting', 'Campaign Management'],
    datasets: [
      {
        data: [45, 25, 15, 10, 5],
        backgroundColor: [
          '#6366f1',
          '#ec4899',
          '#06b6d4',
          '#10b981',
          '#f59e0b',
        ],
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverBorderWidth: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            family: 'Inter',
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#6366f1',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        titleFont: {
          family: 'Inter',
          size: 14,
          weight: '600',
        },
        bodyFont: {
          family: 'Inter',
          size: 13,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 12,
          },
          color: '#64748b',
        },
      },
      y: {
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 12,
          },
          color: '#64748b',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 12,
          },
          color: '#64748b',
          callback: function(value) {
            return value + '%';
          },
        },
      },
    },
  };

  const barOptions = {
    ...chartOptions,
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 12,
          },
          color: '#64748b',
        },
      },
      y: {
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 12,
          },
          color: '#64748b',
          callback: function(value) {
            return value + 'M';
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            family: 'Inter',
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#6366f1',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        callbacks: {
          label: function(context) {
            return context.label + ': ' + context.parsed + '%';
          },
        },
      },
    },
    cutout: '60%',
  };

  const renderChart = () => {
    const chart = charts[activeChart];
    
    switch (chart.type) {
      case 'line':
        return <Line data={growthData} options={chartOptions} />;
      case 'bar':
        return <Bar data={platformData} options={barOptions} />;
      case 'doughnut':
        return <Doughnut data={partnershipData} options={doughnutOptions} />;
      default:
        return <Line data={growthData} options={chartOptions} />;
    }
  };

  return (
    <div className="analytics-dashboard compact">
      <div className="analytics-header">
        <div className="analytics-title">
          <div className="dashboard-icon">📊</div>
          <div>
            <h3>Analytics Dashboard</h3>
            <p>Real-time performance insights</p>
          </div>
        </div>
      </div>

      {/* Chart Navigation */}
      <div className="chart-navigation">
        {charts.map((chart, index) => (
          <button
            key={chart.id}
            className={`chart-nav-btn ${activeChart === index ? 'active' : ''}`}
            onClick={() => setActiveChart(index)}
          >
            <span className="nav-icon">{chart.icon}</span>
            <span className="nav-text">{chart.title}</span>
          </button>
        ))}
      </div>
      
      {/* Active Chart */}
      <div className="chart-container active-chart">
        <div className="chart-header">
          <h4>{charts[activeChart].title}</h4>
          <span className="chart-badge">{charts[activeChart].subtitle}</span>
        </div>
        <div className="chart-wrapper">
          {renderChart()}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="analytics-quick-stats">
        <div className="quick-stat">
          <span className="quick-stat-value">+284%</span>
          <span className="quick-stat-label">Growth</span>
        </div>
        <div className="quick-stat">
          <span className="quick-stat-value">2.4M</span>
          <span className="quick-stat-label">Reach</span>
        </div>
        <div className="quick-stat">
          <span className="quick-stat-value">50+</span>
          <span className="quick-stat-label">Partners</span>
        </div>
        <div className="quick-stat">
          <span className="quick-stat-value">11.5%</span>
          <span className="quick-stat-label">Engagement</span>
        </div>
      </div>
    </div>
  );
};

export default Analytics;